import { useState, useEffect } from 'react'
import { fetchGrowingMaterials, createGrowingMaterial, updateGrowingMaterial, deleteGrowingMaterial } from '../supabase-client'
import { useDataCache } from '../contexts/DataCacheContext'
import { useSuppliers } from '../hooks/useSuppliers'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { OdinButton } from '@/components/ui/odin-button'
import { EditIcon } from '@/components/ui/icons/EditIcon'
import ToggleSwitch from './ToggleSwitch'
import SupplierDialog from './inventory/SupplierDialog'
import { Sprout, Plus } from 'lucide-react'

const CATEGORIES = [
  { value: 'substrate_block', label: 'Substrate Block / Log' },
  { value: 'spawn', label: 'Spawn' },
  { value: 'straw_pellets', label: 'Straw Pellets' },
  { value: 'other', label: 'Other' },
]

// Categories where a strain link is meaningful
const STRAIN_LINKED = ['substrate_block', 'spawn']

const EMPTY_FORM = {
  name: '', category: 'substrate_block', product_id: '', unit_label: 'unit',
  reference_code: '', weight_kg: '', size_dimensions: '', supplier_id: '', active: true,
}

export default function GrowingMaterialsTab() {
  const { invalidateCache, getProducts } = useDataCache()
  const { suppliers, refetch: refetchSuppliers } = useSuppliers({ activeOnly: true })
  const [products, setProducts] = useState([])
  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [editingMaterial, setEditingMaterial] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showSupplierDialog, setShowSupplierDialog] = useState(false)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [mats, prods] = await Promise.all([fetchGrowingMaterials(), getProducts()])
      setMaterials(mats)
      setProducts(prods || [])
    } catch (err) {
      console.error('Failed to load growing materials:', err)
      setError('Failed to load growing materials.')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    setEditingMaterial(null)
    setFormData(EMPTY_FORM)
    setShowDialog(true)
  }

  const handleEdit = (material) => {
    setEditingMaterial(material)
    setFormData({
      name: material.name,
      category: material.category,
      product_id: material.product_id || '',
      unit_label: material.unit_label || 'unit',
      reference_code: material.reference_code || '',
      weight_kg: material.weight_kg != null ? String(material.weight_kg) : '',
      size_dimensions: material.size_dimensions || '',
      supplier_id: material.supplier_id || '',
      active: material.active,
    })
    setShowDialog(true)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) return

    try {
      setSaving(true)
      setError(null)
      const strainLinked = STRAIN_LINKED.includes(formData.category)
      const payload = {
        name: formData.name.trim(),
        category: formData.category,
        product_id: strainLinked ? (formData.product_id || null) : null,
        unit_label: formData.unit_label.trim() || 'unit',
        reference_code: formData.reference_code.trim() || null,
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
        size_dimensions: formData.size_dimensions.trim() || null,
        supplier_id: formData.supplier_id || null,
        active: formData.active,
      }

      if (editingMaterial) {
        await updateGrowingMaterial(editingMaterial.id, payload)
      } else {
        await createGrowingMaterial(payload)
      }

      invalidateCache('growingMaterials')
      await loadData()
      setShowDialog(false)
    } catch (err) {
      console.error('Failed to save growing material:', err)
      setError('Failed to save. ' + (err.message || ''))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this growing material? This cannot be undone.')) return

    try {
      await deleteGrowingMaterial(id)
      invalidateCache('growingMaterials')
      await loadData()
    } catch (err) {
      console.error('Failed to delete growing material:', err)
      setError('Failed to delete. It may be in use by existing sales.')
    }
  }

  const handleSupplierSelect = (value) => {
    if (value === '__create_new__') {
      setShowSupplierDialog(true)
    } else {
      setFormData(prev => ({ ...prev, supplier_id: value === 'none' ? '' : value }))
    }
  }

  const handleSupplierCreated = async (newSupplier) => {
    setShowSupplierDialog(false)
    await refetchSuppliers()
    if (newSupplier?.id) {
      setFormData(prev => ({ ...prev, supplier_id: newSupplier.id }))
    }
  }

  const categoryLabel = (value) => CATEGORIES.find(c => c.value === value)?.label || value
  const strainLinked = STRAIN_LINKED.includes(formData.category)

  if (loading) return <div className="p-4 text-sm text-gray-500">Loading growing materials...</div>

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sprout className="h-5 w-5 text-[#009689]" />
                Growing Materials
              </CardTitle>
              <CardDescription>Configure sellable growing materials (substrate logs, spawn, straw pellets)</CardDescription>
            </div>
            <Button variant="create" onClick={handleAddNew}>
              + Add Material
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Strain</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Ref Code</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                      No growing materials configured. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  materials.map(material => (
                    <TableRow key={material.id}>
                      <TableCell className="font-medium">{material.name}</TableCell>
                      <TableCell className="text-muted-foreground">{categoryLabel(material.category)}</TableCell>
                      <TableCell className="text-muted-foreground">{material.product?.strain || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{material.unit_label}</TableCell>
                      <TableCell className="text-muted-foreground">{material.reference_code || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{material.supplier?.name || '—'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          material.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {material.active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <OdinButton variant="table-action" size="small" leftIcon={<EditIcon />} onClick={() => handleEdit(material)}>Edit</OdinButton>
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(material.id)}>Delete</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMaterial ? 'Edit Growing Material' : 'Add Growing Material'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="materialName">Name</Label>
              <Input
                id="materialName"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Shiitake Logs – 2nd flush"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {strainLinked && (
              <div className="space-y-2">
                <Label>Strain (optional)</Label>
                <Select
                  value={formData.product_id || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, product_id: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a strain" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.strain}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="materialUnit">Unit Label</Label>
              <Input
                id="materialUnit"
                value={formData.unit_label}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_label: e.target.value }))}
                placeholder="e.g. each / bag / log"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="materialRef">Reference Code (for Xero)</Label>
              <Input
                id="materialRef"
                value={formData.reference_code}
                onChange={(e) => setFormData(prev => ({ ...prev, reference_code: e.target.value }))}
                placeholder="e.g. GM-LOG"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="materialWeight">Weight (kg, optional)</Label>
              <Input
                id="materialWeight"
                type="number"
                step="0.1"
                min="0"
                value={formData.weight_kg}
                onChange={(e) => setFormData(prev => ({ ...prev, weight_kg: e.target.value }))}
                placeholder="e.g. 4.5"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="materialDimensions">Size Dimensions (optional)</Label>
              <Input
                id="materialDimensions"
                value={formData.size_dimensions}
                onChange={(e) => setFormData(prev => ({ ...prev, size_dimensions: e.target.value }))}
                placeholder="e.g. 20x15x50cm"
              />
            </div>
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={handleSupplierSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                  <Separator className="my-1" />
                  <SelectItem value="__create_new__">
                    <span className="flex items-center gap-1.5 text-[#009689]">
                      <Plus className="h-3.5 w-3.5" />
                      Create new supplier
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <ToggleSwitch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !formData.name.trim()}>
              {saving ? 'Saving...' : editingMaterial ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupplierDialog
        open={showSupplierDialog}
        onOpenChange={setShowSupplierDialog}
        supplier={null}
        onSuccess={handleSupplierCreated}
      />
    </>
  )
}
